
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Edit2, Save, X, Palette, RefreshCw, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { ProbabilityEditor } from "./ProbabilityEditor";
import { ProbabilityRangeEditor } from "./ProbabilityRangeEditor";
import { CellEditor, type Cell } from "./CellEditor";
import { toast } from "sonner";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { handleEditMatrixWithSafeguards, isEditingInProgress, clearEditingLock } from "@/lib/classification-matrix-frontend-v2";
import { createAsyncEditHandler, defaultConfig as asyncEditConfig } from "@/lib/async-matrix-edit-handler";

// ENTREGÁVEL A: Single source of truth + Edição inline
// Componente único sem remount ao entrar em edição
export function ClassificationMatrixTab() {
  // ===== ESTADO ÚNICO (viewModel) =====
  const [isEditing, setIsEditing] = useState(false);
  const [publishedVersionId, setPublishedVersionId] = useState<number | null>(null);
  const [draftVersionId, setDraftVersionId] = useState<number | null>(null);
  const [version, setVersion] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  
  // Dados carregados uma única vez
  const [probabilities, setProbabilities] = useState<any[]>([]);
  const [severities, setSeverities] = useState<any[]>([]);
  const [cells, setCells] = useState<any[]>([]);
  
  // Dados em edição (cópia para edição inline)
  const [editedProbabilities, setEditedProbabilities] = useState<any[]>([]);
  const [editedCells, setEditedCells] = useState<any[]>([]);
  
  // Flags de estado
  const [isDirty, setIsDirty] = useState(false);
  const [requiresCellRebuild, setRequiresCellRebuild] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  
  // Editor de célula (painel lateral)
  const [selectedCell, setSelectedCell] = useState<Cell | null>(null);
  const [isCellEditorOpen, setIsCellEditorOpen] = useState(false);
  
  // HOTFIX: AbortController para cancelar mutação em-flight
  const abortControllerRef = React.useRef<AbortController | null>(null);
  
  // NEW: State machine para editação assíncrona
  const [editorState, setEditorState] = useState<'idle' | 'opening' | 'preparingDraft' | 'ready' | 'error'>('idle');
  const [editorError, setEditorError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const asyncEditHandlerRef = React.useRef(createAsyncEditHandler(asyncEditConfig));
  
  // Feature flag: FF_ASYNC_MATRIX_EDIT (default OFF)
  const FF_ASYNC_MATRIX_EDIT = true; // ✅ ATIVADO: novo state machine assíncrono com retry
  
  // Versão efetiva (PUBLISHED em view, DRAFT em edit)
  const effectiveVersionId = isEditing ? draftVersionId : publishedVersionId;
  
  // ===== QUERIES COM CACHE OTIMIZADO =====
  const getCurrentMatrix = trpc.ergos.classificationMatrix.getCurrentRiskMatrix.useQuery(
    {},
    {
      keepPreviousData: true,
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );
  
  // Detectar quando revalidação em background está ocorrendo
  useEffect(() => {
    setIsRevalidating(getCurrentMatrix.isFetching);
  }, [getCurrentMatrix.isFetching]);
  
  const getPublishedQualifiers = trpc.ergos.classificationMatrix.getPublishedQualifiers.useQuery(
    {},
    {
      keepPreviousData: true,
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
    }
  );
  
  // ===== MUTATIONS =====
  const ensureDraftMutation = trpc.ergos.classificationMatrix.ensureClassificationDraft.useMutation();
  const saveMutation = trpc.ergos.classificationMatrix.saveRiskMatrixDraft.useMutation();
  const publishMutation = trpc.ergos.classificationMatrix.publishRiskMatrix.useMutation();
  const rebuildMutation = trpc.ergos.classificationMatrix.rebuildClassificationMatrixCells.useMutation();
  const cancelDraftEditMutation = trpc.ergos.classificationMatrix.cancelClassificationDraftEdit.useMutation();
  const utils = trpc.useUtils();
  
  // ===== EFEITOS =====
  
  // Carregar dados iniciais (versão PUBLISHED)
  useEffect(() => {
    if (getCurrentMatrix.data) {
      const { version: versionData, probabilities: probs, severities: sevs, cells: cellsData } = getCurrentMatrix.data;
      setVersion(versionData.version);
      setStatus(versionData.status);
      setPublishedVersionId(versionData.id);
      setProbabilities(probs);
      setSeverities(sevs);
      setCells(cellsData);
    }
  }, [getCurrentMatrix.data]);
  
  // Beforeunload warning se dirty
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
  
  // ===== HANDLERS =====
  
  // Iniciar edição - Criar/reutilizar DRAFT único
  // FASE 5-7: Com timeout, cache invalidation, e prevenção de duplo clique
  // NEW: Com state machine assíncrono e retry (FF_ASYNC_MATRIX_EDIT)
  const handleEditMatrix = async () => {
    if (FF_ASYNC_MATRIX_EDIT) {
      // NOVO: Fluxo assíncrono com state machine
      const handler = asyncEditHandlerRef.current;
      
      await handler.openEditor(
        ensureDraftMutation,
        utils,
        {
          onStateChange: (state: string, error?: string) => {
            setEditorState(state as any);
            if (error) setEditorError(error);
            console.log(`[handleEditMatrix] Estado: ${state}`, error ? `(erro: ${error})` : '');
          },
          onSuccess: (result: any) => {
            if (result) {
              setDraftVersionId(result.draftVersionId);
              setVersion(result.version.version);
              setStatus(result.version.status);
              setIsEditing(true);
              setEditedProbabilities(JSON.parse(JSON.stringify(result.probabilities || [])));
              setEditedCells(JSON.parse(JSON.stringify(result.cells || [])));
              setSeverities(JSON.parse(JSON.stringify(result.severities || [])));
              setIsDirty(false);
              setRequiresCellRebuild(false);
              setEditorError(null);
            }
          },
          onError: (error: any) => {
            console.error('[handleEditMatrix] Erro:', error.message);
          },
          onCancel: () => {
            setEditorState('idle');
            setEditorError(null);
          },
        }
      );
    } else {
      // LEGADO: Fluxo antigo com timeout fatal (para rollback)
      const controller = new AbortController();
      abortControllerRef.current = controller;
      
      await handleEditMatrixWithSafeguards(
        ensureDraftMutation,
        utils,
        {
          onSuccess: (result: any) => {
            if (result) {
              setDraftVersionId(result.draftVersionId);
              setVersion(result.version.version);
              setStatus(result.version.status);
              setIsEditing(true);
              setEditedProbabilities(JSON.parse(JSON.stringify(result.probabilities || [])));
              setEditedCells(JSON.parse(JSON.stringify(result.cells || [])));
              setSeverities(JSON.parse(JSON.stringify(result.severities || [])));
              setIsDirty(false);
              setRequiresCellRebuild(false);
              abortControllerRef.current = null;
            }
          },
          onError: (error: any) => {
            setIsEditing(false);
            setDraftVersionId(null);
            setEditedProbabilities([]);
            setEditedCells([]);
            abortControllerRef.current = null;
          },
          onFinally: () => {
            // Nada a fazer
          },
        }
      );
    }
  };
  
  // Cancelar edição - CORREÇÃO: Cancelamento REAL com liberação de lock no backend
  const handleCancelEdit = async () => {
    setIsCleaningUp(true);
    console.log("[handleCancelEdit] INICIANDO cancelamento REAL");
    
    try {
      // PASSO 1: Cancelar fluxo assíncrono se ativo (abort request HTTP)
      if (FF_ASYNC_MATRIX_EDIT) {
        const handler = asyncEditHandlerRef.current;
        handler.cancel();
        console.log("[handleCancelEdit] Handler assíncrono cancelado");
        
        // CORREÇÃO CRÍTICA: Criar NOVO handler para próxima tentativa
        // Isso garante que o AbortController anterior não seja reutilizado
        asyncEditHandlerRef.current = createAsyncEditHandler(asyncEditConfig);
        console.log("[handleCancelEdit] NOVO handler criado para próxima tentativa");
      }
      
      // PASSO 2: Abortar request pendente (legado)
      if (abortControllerRef.current) {
        console.log("[handleCancelEdit] Abortando request em-flight (legado)");
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // PASSO 3: Chamar backend para liberar lock
      console.log("[handleCancelEdit] Liberando lock no backend...");
      try {
        await cancelDraftEditMutation.mutateAsync();
        console.log("[handleCancelEdit] Lock liberado no backend com sucesso");
      } catch (error: any) {
        console.warn("[handleCancelEdit] Erro ao liberar lock no backend (continuando):", error);
      }
      
      // PASSO 4: Limpar lock local
      clearEditingLock();
      console.log("[handleCancelEdit] Lock local liberado");
      
      // PASSO 5: Reset estado de UI
      setIsEditing(false);
      setEditedCells([]);
      setEditedProbabilities([]);
      setIsDirty(false);
      setSelectedCell(null);
      setIsCellEditorOpen(false);
      
      // PASSO 6: Reset state machine
      setEditorState('idle');
      setEditorError(null);
      setRetryCount(0);
      
      console.log("[handleCancelEdit] Cancelamento REAL completo - sistema em idle");
    } finally {
      // Desabilitar botão por 300ms para garantir cleanup
      setTimeout(() => {
        setIsCleaningUp(false);
        console.log("[handleCancelEdit] Botão habilitado novamente");
      }, 300);
    }
  };
  
  // NOVO: Handler para retry
  const handleRetryEdit = async () => {
    setRetryCount(prev => prev + 1);
    await handleEditMatrix();
  };
  
  // Salvar rascunho
  const handleSaveDraft = async () => {
    if (!effectiveVersionId) {
      toast.error("Versão não encontrada");
      return;
    }

    try {
      // [PXS_FLOW] ETAPA 1: Log antes de enviar
      const payloadHash = JSON.stringify(editedProbabilities.map((p: any) => `${p.code}:${p.probabilityValue}:${p.minHours}:${p.maxHours}`)).substring(0, 50);
      console.log(`[PXS_FLOW] SAVE_DRAFT_BEFORE | versionId=${effectiveVersionId} | probs=${editedProbabilities.length} | hash=${payloadHash}`);
      
      const result = await saveMutation.mutateAsync({
        versionId: effectiveVersionId,
        probabilities: editedProbabilities,  // ADICIONADO: passar probabilities
        cells: editedCells,
      });

      if (result && result.version && result.probabilities && result.cells) {
        // [PXS_FLOW] ETAPA 1: Log após receber resposta
        const responseHash = JSON.stringify(result.probabilities.map((p: any) => `${p.code}:${p.probabilityValue}:${p.minHours}:${p.maxHours}`)).substring(0, 50);
        console.log(`[PXS_FLOW] SAVE_DRAFT_AFTER | versionId=${result.version.id} | probs=${result.probabilities.length} | hash=${responseHash}`);
        
        // Sincronizar estado com dados retornados do backend
        setVersion(result.version.version);
        setStatus(result.version.status);
        setProbabilities(result.probabilities);  // ADICIONADO: sincronizar probabilities
        setEditedProbabilities(JSON.parse(JSON.stringify(result.probabilities)));
        setCells(result.cells);
        setEditedCells(JSON.parse(JSON.stringify(result.cells)));
        setIsDirty(false);
        
        // Detectar se mudou quantidade/ordem de probabilidades
        const probsChanged = JSON.stringify(probabilities) !== JSON.stringify(result.probabilities);
        if (probsChanged) {
          console.log(`[PXS_FLOW] SAVE_DRAFT_REBUILD_NEEDED | probs_changed=true`);
          setRequiresCellRebuild(true);
          toast.success("Rascunho salvo. Reconstrução necessária.");
        } else {
          console.log(`[PXS_FLOW] SAVE_DRAFT_SUCCESS | probs_changed=false`);
          toast.success("Rascunho salvo com sucesso");
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao salvar rascunho");
    }
  };
  
  // Publicar
  const handlePublish = async () => {
    if (!effectiveVersionId) {
      toast.error("Versão não encontrada");
      return;
    }

    try {
      // [PXS_FLOW] ETAPA 1: Log antes de publicar
      const beforeHash = JSON.stringify(editedProbabilities.map((p: any) => `${p.code}:${p.probabilityValue}:${p.minHours}:${p.maxHours}`)).substring(0, 50);
      console.log(`[PXS_FLOW] PUBLISH_BEFORE | draftVersionId=${effectiveVersionId} | probs=${editedProbabilities.length} | hash=${beforeHash}`);
      
      const result = await publishMutation.mutateAsync({ versionId: effectiveVersionId });
      if (result) {
        // [PXS_FLOW] ETAPA 1: Log apos publicar
        const afterHash = JSON.stringify(result.probabilities.map((p: any) => `${p.code}:${p.probabilityValue}:${p.minHours}:${p.maxHours}`)).substring(0, 50);
        console.log(`[PXS_FLOW] PUBLISH_AFTER | publishedVersionId=${result.version.id} | probs=${result.probabilities.length} | hash=${afterHash}`);
        
        // Atualizar estado local com dados retornados (sem invalidação agressiva)
        setVersion(result.version.version);
        setStatus(result.version.status);
        setProbabilities(result.probabilities || []);
        setSeverities(result.severities || []);
        setCells(result.cells || []);
        setPublishedVersionId(result.version.id);
        setIsEditing(false);
        setEditedCells([]);
        setEditedProbabilities([]);
        setIsDirty(false);
        setSelectedCell(null);
        setIsCellEditorOpen(false);
        setRequiresCellRebuild(false);
        console.log(`[PXS_FLOW] PUBLISH_SUCCESS | publishedVersionId=${result.version.id}`);
        toast.success("Matriz publicada com sucesso");
        
        // Invalidação seletiva (não agressiva)
        // Invalidar query principal que carrega a tela
        console.log('[handlePublish] Invalidando getCurrentRiskMatrix para refetch...');
        try {
          utils.ergos.classificationMatrix.getCurrentRiskMatrix.invalidate();
          // Disparar refetch explícito
          await getCurrentMatrix.refetch();
          console.log('[handlePublish] getCurrentRiskMatrix invalidado e refetch disparado');
        } catch (e) {
          console.error('[handlePublish] Erro ao invalidar getCurrentRiskMatrix:', e);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao publicar");
      console.error('[handlePublish] Erro ao publicar:', error);
    }
  };
  
  // Reconstruir grade
  const handleRebuildCells = async () => {
    if (!effectiveVersionId) {
      toast.error("Versão não encontrada");
      return;
    }

    try {
      setIsRebuilding(true);
      const result = await rebuildMutation.mutateAsync({ versionId: effectiveVersionId });
      toast.success(`Reconstruído: ${result.createdCount} criadas, ${result.updatedCount} atualizadas`);
      setRequiresCellRebuild(false);
    } catch (error: any) {
      toast.error(error.message || "Erro ao reconstruir grade");
    } finally {
      setIsRebuilding(false);
    }
  };
  
  // Atualizar célula (painel lateral)
  const handleCellChange = useCallback((updatedCell: Cell) => {
    const updated = [...editedCells];
    let idx = updated.findIndex(
      (c) =>
        c.probabilityCode === updatedCell.probabilityCode &&
        c.severityCode === updatedCell.severityCode
    );
    if (idx >= 0) {
      updated[idx] = updatedCell;
    } else {
      updated.push(updatedCell);
    }
    setEditedCells(updated);
    setIsDirty(true);
  }, [editedCells]);
  
  // ===== MEMOIZAÇÕES =====
  
  // Sanitizar probabilidades (dedup + filter + sort)
  const sanitizeProbabilities = (probs: any[]) => {
    const seen = new Map<string, any>();
    for (const prob of probs) {
      const key = `${prob.code}`;
      if (!seen.has(key)) {
        seen.set(key, prob);
      }
    }
    
    let result = Array.from(seen.values())
      .filter((p) => p.isActive !== false)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    
    result = result.filter((p) => /^P\d+$/.test(p.code || ''));
    
    return result;
  };
  
  // Dados exibidos (view vs edit)
  const displayProbabilities = useMemo(
    () => sanitizeProbabilities(isEditing ? editedProbabilities : probabilities),
    [isEditing, editedProbabilities, probabilities]
  );
  
  const displayCells = useMemo(
    () => (isEditing ? editedCells : cells),
    [isEditing, editedCells, cells]
  );
  
  // Grid estrutural (cross join)
  const gridRows = useMemo(() => {
    return displayProbabilities.map((prob) => ({
      prob,
      cells: severities.map((sev) => {
        const cell = displayCells.find(
          (c) =>
            c.probabilityCode === prob.code &&
            c.severityCode === sev.code
        );
        const score = (prob.probabilityValue || 1) * (sev.weight || 1);
        return { sev, cell, score };
      }),
    }));
  }, [displayProbabilities, severities, displayCells]);
  
  // ===== RENDER =====
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold">Matriz de Classificação (P × S)</h2>
            {isRevalidating && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full animate-pulse">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                Atualizando...
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            {version} - Status: {status}
          </p>
          {isEditing && (
            <p className="text-sm text-blue-600 font-semibold">
              Você está editando um rascunho
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {!isEditing && (
            <>
              <Button 
                onClick={handleEditMatrix} 
                variant="default"
                disabled={isEditingInProgress() || editorState !== 'idle' || isCleaningUp}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {isCleaningUp && "Limpando..."}
                {editorState === 'opening' && "Abrindo..."}
                {editorState === 'preparingDraft' && "Preparando..."}
                {editorState === 'error' && "Erro - Tentar Novamente"}
                {editorState === 'idle' && !isCleaningUp && (isEditingInProgress() ? "Carregando..." : "Editar Matriz")}
              </Button>
              {editorState === 'error' && (
                <Button 
                  onClick={handleRetryEdit} 
                  variant="outline"
                  disabled={retryCount >= 3}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry ({retryCount}/3)
                </Button>
              )}
            </>
          )}
          {isEditing && (
            <>
              <Button 
                onClick={handleSaveDraft} 
                variant="default" 
                disabled={!isEditing || saveMutation.isPending}
              >
                <Save className="w-4 h-4 mr-2" />
                {saveMutation.isPending ? 'Salvando...' : 'Salvar Rascunho'}
              </Button>
              <Button 
                onClick={handlePublish} 
                variant="default" 
                disabled={!isEditing || hasValidationErrors || publishMutation.isPending}
                title={hasValidationErrors ? 'Corrija os erros de validação antes de publicar' : ''}
              >
                <Save className="w-4 h-4 mr-2" />
                {publishMutation.isPending ? 'Publicando...' : 'Publicar'}
              </Button>
              <Button onClick={handleCancelEdit} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Alert para estado de erro */}
      {editorState === 'error' && editorError && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <strong>Erro ao preparar matriz:</strong> {editorError}
            <div className="mt-2">
              <Button 
                onClick={handleRetryEdit} 
                size="sm"
                variant="outline"
                disabled={retryCount >= 3}
              >
                Tentar Novamente ({retryCount}/3)
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Alert para estado de carregamento */}
      {(editorState === 'opening' || editorState === 'preparingDraft') && (
        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            {editorState === 'opening' && "Abrindo editor de matriz..."}
            {editorState === 'preparingDraft' && "Preparando dados para edição (pode levar alguns segundos)..."}
          </AlertDescription>
        </Alert>
      )}
      
      {/* Banner de reconstrução necessária */}
      {requiresCellRebuild && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            Há registros antigos fora da matriz atual. Clique em "Reconstruir grade" para remover inconsistências.
            <Button 
              onClick={handleRebuildCells} 
              disabled={isRebuilding}
              className="ml-2"
              variant="outline"
              size="sm"
            >
              {isRebuilding ? "Reconstruindo..." : "Reconstruir grade"}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Bloco de Regra Metodológica */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-lg">Regra de Classificação de Risco</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-700">
          <p><strong>Probabilidade (P)</strong> é definida pela hora extra média diária</p>
          <p><strong>Severidade (S)</strong> é definida pelo Qualificador ERGOS</p>
          <p><strong>Classificação do risco</strong> = P × S</p>
        </CardContent>
      </Card>
      
      {/* Seção de Probabilidades - Nova UX Intuitiva */}
      {effectiveVersionId && isEditing && (
        <ProbabilityRangeEditor
          probabilities={editedProbabilities}
          onChange={setEditedProbabilities}
          disabled={false}
          isLoading={saveMutation.isPending}
          onValidationChange={setHasValidationErrors}
        />
      )}
      
      {/* Visualização em modo read-only */}
      {effectiveVersionId && !isEditing && (
        <ProbabilityRangeEditor
          probabilities={probabilities}
          onChange={() => {}}
          disabled={true}
          isLoading={false}
        />
      )}
      
      {/* Mapeamento ERGOS - Severidade (Read-only) */}
      <Card className="bg-gray-50 border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg">Mapeamento ERGOS - Severidade (S)</CardTitle>
          <CardDescription className="text-xs text-gray-600 mt-2">
            Derivado automaticamente da Matriz de Qualificacao ERGOS. Para alterar, acesse a aba correspondente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border p-2 bg-gray-200 font-semibold text-left">Qualificador ERGOS</th>
                  <th className="border p-2 bg-gray-200 font-semibold text-center">Severidade</th>
                </tr>
              </thead>
              <tbody>
                {severities.map((sev) => (
                  <tr key={sev.id}>
                    <td className="border p-2">{sev.label}</td>
                    <td className="border p-2 text-center font-semibold">{sev.weight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Grid de Classificação */}
      <Card>
        <CardHeader>
          <CardTitle>Grid de Classificação (P × S)</CardTitle>
          <CardDescription>
            {isEditing ? "Clique em uma célula para editar" : "Visualização"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="border p-2 bg-gray-100 font-semibold text-left">Probabilidade</th>
                  {severities.map((sev) => (
                    <th key={sev.id} className="border p-2 bg-gray-100 font-semibold text-center min-w-[200px]">
                      {sev.label} ({sev.code})
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gridRows.map((row) => (
                  <tr key={row.prob.id || row.prob.code}>
                    <td className="border p-2 bg-gray-50 font-medium">{row.prob.label} ({row.prob.code})</td>
                    {row.cells.map(({ sev, cell, score }) => {
                      const handleCellClick = () => {
                        if (isEditing) {
                          const cellToEdit = cell || {
                            id: `new_${Date.now()}_${row.prob.code}_${sev.code}`,
                            versionId: effectiveVersionId,
                            probabilityCode: row.prob.code,
                            severityCode: sev.code,
                            score,
                            riskLevelLabel: "",
                            conclusionText: "",
                            colorHex: "#94a3b8",
                          };
                          setSelectedCell(cellToEdit);
                          setIsCellEditorOpen(true);
                        }
                      };

                      return (
                        <td
                          key={`${row.prob.code}-${sev.code}`}
                          className={`border p-2 ${isEditing ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                          onClick={handleCellClick}
                        >
                          <div
                            className="p-3 rounded text-center text-white font-semibold"
                            style={{ backgroundColor: cell?.colorHex || "#ffffff" }}
                          >
                            <p className="text-lg">{score}</p>
                            <p className="text-xs">{cell?.riskLevelLabel || "-"}</p>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Editor de célula em painel lateral */}
      {isCellEditorOpen && selectedCell && (
        <div className="fixed right-0 top-0 h-screen w-96 bg-white shadow-lg border-l z-50 overflow-y-auto">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-bold">Editar Célula</h3>
            <Button 
              onClick={() => setIsCellEditorOpen(false)} 
              variant="ghost" 
              size="sm"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-4">
            <CellEditor 
              cell={selectedCell} 
              versionId={effectiveVersionId || 0}
              isOpen={true}
              onChange={handleCellChange}
              onClose={() => setIsCellEditorOpen(false)}
              probability={displayProbabilities.find(p => p.code === selectedCell?.probabilityCode)}
              severity={severities.find(s => s.code === selectedCell?.severityCode)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
